import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { alert } from '../../../hooks/function';
import { Box, Chip, FormControlLabel, Switch, TextField, Typography } from '../../ui';

/** Default recipient used when instructor messaging is first enabled.
 *  Routes learner questions to the support inbox unless an admin
 *  explicitly redirects them. */
const DEFAULT_QUESTION_EMAIL = 'support@dentlogics.com';

/** Cheap-and-correct email validator for the in-field confirmation
 *  indicator. The TextField's `type="email"` does the heavy lifting;
 *  this just decides when to flip the green check on. */
function isPlausibleEmail(s: string): boolean {
  const v = s.trim();
  if (!v) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/**
 * Instructor messaging control — lets learners email questions about the
 * course directly to the instructor. Required for CE accreditation in
 * many cases.
 *
 * The persisted values (`enabled`/`email`) are owned by the parent form so
 * they save with the rest of the course settings. The chip/edit toggle and
 * in-progress draft are ephemeral UI state kept local here — the parent
 * remounts this via `key` when the edited course changes, resetting them.
 */
export default function InstructorMessagingField({
  enabled,
  email,
  onEnabledChange,
  onEmailChange,
}: {
  enabled: boolean;
  email: string;
  onEnabledChange: (enabled: boolean) => void;
  onEmailChange: (email: string) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  // Chip mode when an email exists; edit mode when empty so the input is
  // ready for typing.
  const [isEditingEmail, setIsEditingEmail] = useState(!email);
  const [emailDraft, setEmailDraft] = useState('');

  const commitDraft = () => {
    const v = emailDraft.trim();
    if (!isPlausibleEmail(v)) return;
    onEmailChange(v);
    setEmailDraft('');
    setIsEditingEmail(false);
    alert(enqueueSnackbar, `Question email set to ${v}`, 'success');
  };
  const cancelDraft = () => {
    setEmailDraft('');
    // If we still have a committed email, drop back to chip mode; otherwise
    // leave the input visible so the admin can try again.
    if (email.trim()) setIsEditingEmail(false);
  };
  const showChip = !!email.trim() && !isEditingEmail;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={enabled}
              onChange={(e) => {
                const next = e.target.checked;
                onEnabledChange(next);
                // First-time enable + no email on file → seed the default
                // support inbox so the admin gets a working address out of
                // the box. The chip below makes it visibly removable.
                // Persisted on the next Save (settings payload), like the
                // rest of the form.
                if (next && !email.trim()) {
                  onEmailChange(DEFAULT_QUESTION_EMAIL);
                  setIsEditingEmail(false);
                  setEmailDraft('');
                }
              }}
            />
          }
          label={
            <Typography variant="body2" fontWeight={600}>
              Enable instructor messaging
            </Typography>
          }
          sx={{ mr: 0 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
          Shows an "Ask a question" form on the course player.
        </Typography>
      </Box>

      {enabled && (
        <Box>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
            Question email
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              minHeight: 40,
              px: 1.25,
              py: 0.75,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            {showChip && (
              <Chip
                label={email}
                onDelete={() => {
                  onEmailChange('');
                  setEmailDraft('');
                  setIsEditingEmail(true);
                }}
                size="medium"
                sx={{ fontWeight: 600 }}
              />
            )}
            {!showChip && (
              <TextField
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitDraft();
                  } else if (e.key === 'Escape') {
                    cancelDraft();
                  }
                }}
                onBlur={commitDraft}
                placeholder={DEFAULT_QUESTION_EMAIL}
                type="email"
                variant="standard"
                autoFocus
                fullWidth
                sx={{ flex: 1, minWidth: 200 }}
                InputProps={{
                  disableUnderline: true,
                  sx: { fontSize: '0.95rem' },
                }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Where learner questions will be sent. Defaults to{' '}
            <strong>{DEFAULT_QUESTION_EMAIL}</strong> — remove the chip with ✕ to enter a different
            address.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
